import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { FiAlertTriangle } from "react-icons/fi";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import Modal from "newui/components/ui/Modal/Modal";
import { selectCurrentUserId } from "newui/features/Auth/authSelectors";
import { PanelEvaluationView } from "../InitialEvaluationStep/panel/PanelEvaluationView";
import { FinalScoringContent, useFinalScoringState } from "./FinalScoringContent";
import { PanelMeetingStep } from "../PanelMeetingStep";
import { usePanelMemberStatus } from "../../../hooks/usePanelScoringQueries";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { PanelMode } from "../../../types/panel.type";
import type { EvaluationStepNumber } from "../../../types/evaluation.type";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { finalEvaluationLeadSteps, panelMeetingLandingSteps } from "guides/features/Evaluation";
import { useCurrentUserId } from "hooks/auth";

interface FinalEvaluationStepProps {
	currentStep?: EvaluationStepNumber;
	evalId?: number;
	// Visibility-only lead override forwarded from RFxEvaluationSteps. True when a
	// switched executive is viewing as RFx Lead (read-only). Reveals the lead view
	// only; writes stay blocked by the read-only wrapper + backend policy.
	isRfxLeadView?: boolean;
	isLoading?: boolean;
	panelMemberId?: number;
	panelMemberStatus?: string;
	panelMode?: PanelMode | string | null;
	rfxData: Rfx;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
	onRefreshUserContext?: () => void;
}

export const FinalEvaluationStep: React.FC<FinalEvaluationStepProps> = ({
	rfxData,
	evalId,
	isRfxLeadView = false,
	panelMode,
	panelMemberId,
	panelMemberStatus,
	isLoading = false,
	onBeforeNext,
	onCompletionChange,
	onRefreshUserContext,
}) => {
	const rfxId = rfxData?.id;
	const currentUserId = useSelector(selectCurrentUserId);
	const isPanel = panelMode === "manual_panel" || panelMode === "ai_panel";
	const isRfxLead = currentUserId !== null && rfxData?.rfxLead?.id === currentUserId;
	// Visibility-only override for a switched executive in "View as RFx Lead"
	// read-only mode. Writes remain blocked by the EvaluationPage read-only
	// wrapper and the backend policy; this only affects what renders.
	const effectiveIsRfxLead = isRfxLead || isRfxLeadView;

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	if (isPanel && evalId) {
		return (
			<FinalPanelLayout
				rfxData={rfxData}
				rfxId={rfxId}
				evalId={evalId}
				isRfxLead={effectiveIsRfxLead}
				panelMemberId={panelMemberId}
				panelMemberStatus={panelMemberStatus}
				onBeforeNext={onBeforeNext}
				onCompletionChange={onCompletionChange}
				onRefreshUserContext={onRefreshUserContext}
			/>
		);
	}

	return (
		<FinalSoloView
			rfxData={rfxData}
			rfxId={rfxId}
			evalId={evalId}
			onBeforeNext={onBeforeNext}
			onCompletionChange={onCompletionChange}
		/>
	);
};

interface FinalSoloViewProps {
	evalId?: number;
	rfxData: Rfx;
	rfxId: number;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
}

const FinalSoloView: React.FC<FinalSoloViewProps> = ({
	rfxData,
	rfxId,
	evalId,
	onCompletionChange,
}) => {
	const effectiveEvalId = evalId ?? 0;
	const readOnlyLabel = `Viewing final evaluation scores. Only the RFx Lead${rfxData?.rfxLead?.fullName ? ` (${rfxData.rfxLead.fullName})` : ""} can modify final scores.`;

	return (
		<div data-testid="final-evaluation-step">
			<FinalScoringContent
				rfxId={rfxId}
				evalId={effectiveEvalId}
				readOnlyLabel={readOnlyLabel}
				onSubmitted={() => onCompletionChange?.(true)}
			/>
		</div>
	);
};

/**
 * Wrapper for panel mode that gates the Panel Meeting section on all active
 * members having submitted their final scores (Bug 1 — PRO-572 review).
 */
interface FinalPanelLayoutProps {
	evalId: number;
	isRfxLead: boolean;
	panelMemberId?: number;
	panelMemberStatus?: string;
	rfxData: Rfx;
	rfxId: number;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
	onRefreshUserContext?: () => void;
}

const FinalPanelLayout: React.FC<FinalPanelLayoutProps> = ({
	rfxData,
	rfxId,
	evalId,
	isRfxLead,
	panelMemberId,
	panelMemberStatus,
	onBeforeNext,
	onCompletionChange,
	onRefreshUserContext,
}) => {
	const { data: members } = usePanelMemberStatus(rfxId, evalId, "final");

	// Only show the panel meeting section once ALL active members have submitted
	// their final scores (active = not removed, not declined, not revoked).
	const activeMembers = (members ?? []).filter((m) => !m.is_removed);
	const allFinalScoresSubmitted =
		activeMembers.length > 0 &&
		activeMembers.every(
			(m) => m.status === "declined" || m.status === "revoked" || m.final_status === "submitted"
		);

	const meetingSection = allFinalScoresSubmitted ? (
		<div
			className="mb-8 border-b border-gray-200 pb-6"
			data-testid="final-scoring-meeting-section"
		>
			{isRfxLead && (
				<InstructionsInfoBox
					title="Panel Meeting & Award"
					instructions={PANEL_MEETING_INSTRUCTIONS}
					guideSteps={panelMeetingLandingSteps}
					data-testid="panel-meeting-info-box"
				/>
			)}
			<h3 className="mt-4 text-base font-semibold text-gray-900 mb-4">Panel Meeting</h3>
			<PanelMeetingStep rfxId={rfxId} evalId={evalId} isRfxLead={isRfxLead} />
		</div>
	) : null;

	if (isRfxLead) {
		return (
			<div>
				{meetingSection}
				<FinalPanelLeadView
					rfxData={rfxData}
					rfxId={rfxId}
					evalId={evalId}
					allFinalScoresSubmitted={allFinalScoresSubmitted}
					onBeforeNext={onBeforeNext}
					onCompletionChange={onCompletionChange}
					onRefreshUserContext={onRefreshUserContext}
				/>
			</div>
		);
	}

	if (panelMemberId && panelMemberStatus) {
		return (
			<div>
				{meetingSection}
				<FinalPanelMemberView
					rfxId={rfxId}
					evalId={evalId}
				/>
			</div>
		);
	}

	return (
		<div className="space-y-4" data-testid="final-evaluation-read-only">
			<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
				<span className="mt-0.5 text-amber-600" aria-hidden="true">&#9888;</span>
				<div>
					<p className="text-sm font-medium text-amber-800">
						You are not a member of this evaluation panel
					</p>
					<p className="mt-1 text-sm text-amber-700">
						Only the RFx Lead
						{rfxData?.rfxLead?.fullName && ` (${rfxData.rfxLead.fullName})`}
						{" "}and invited panel members can access final scoring.
					</p>
				</div>
			</div>
		</div>
	);
};

interface FinalPanelLeadViewProps {
	allFinalScoresSubmitted: boolean;
	evalId: number;
	rfxData: Rfx;
	rfxId: number;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
	onRefreshUserContext?: () => void;
}

type FinalLeadTabId = "my-final-scores" | "panel-progress";

const FINAL_LEAD_INSTRUCTIONS: React.ReactNode[] = [
	"Submit your own final scores on the 'My Final Scores' tab.",
	"Monitor panel member progress on the 'Panel Members' tab — you can send reminders or revoke access for members who won't be submitting.",
	"Once all members have submitted, a Panel Meeting section will appear where you can schedule a meeting to reach consensus scores or skip directly to the award decision.",
];

const PANEL_MEETING_INSTRUCTIONS: React.ReactNode[] = [
	"All panel members have submitted their final scores.",
	"Schedule a panel meeting to discuss and agree on consensus scores with your panel, or skip the meeting and proceed directly to the final decision.",
	"Once consensus scores are finalised, you will move to the Final Report step to select a preferred supplier and notify suppliers.",
];

const FinalPanelLeadView: React.FC<FinalPanelLeadViewProps> = ({
	rfxData,
	rfxId,
	evalId,
	allFinalScoresSubmitted,
	onBeforeNext,
	onCompletionChange,
	onRefreshUserContext,
}) => {
	const state = useFinalScoringState(rfxId, evalId);
	const [activeTab, setActiveTab] = useState<FinalLeadTabId>(state.isSubmitted ? "panel-progress" : "my-final-scores");
	const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
	const { data: members } = usePanelMemberStatus(rfxId, evalId, "final");

	/**
	 * PRO-664: the Lead's panel-member row is auto-created server-side, so its
	 * existence never meant "the Lead is scoring" — `is_scoring` is the opt-in. A Lead
	 * who chose not to score was still shown "My Final Scores" and was still counted
	 * as an outstanding scorer in the incomplete-scores warning.
	 */
	const leadIsScoring = (members ?? []).some(
		(m) => m.role === "rfxLead" && !m.is_removed && m.is_scoring === true
	);

	/** A member who is actually expected to submit final scores. */
	const isExpectedToScore = useCallback(
		(m: { is_removed: boolean; is_scoring: boolean; role: string }) =>
			!m.is_removed && (m.role !== "rfxLead" || m.is_scoring === true),
		[]
	);

	// `members` arrives asynchronously, so the initial tab is chosen before we know
	// whether the Lead is scoring. Never leave the selection on a hidden tab.
	const effectiveTab: FinalLeadTabId =
		!leadIsScoring && activeTab === "my-final-scores" ? "panel-progress" : activeTab;

	const resolveRef = useRef<((proceed: boolean) => void) | null>(null);

	const handleBeforeNext = useCallback(async (): Promise<boolean> => {
		const activeMembers = (members ?? []).filter(isExpectedToScore);
		const unsubmitted = activeMembers.filter(
			(m) => m.status !== "declined" && m.status !== "revoked" && m.final_status !== "submitted"
		);

		if (unsubmitted.length > 0) {
			setShowIncompleteWarning(true);
			return new Promise<boolean>((resolve) => {
				resolveRef.current = resolve;
			});
		}

		return true;
	}, [members, isExpectedToScore]);

	useEffect(() => {
		onBeforeNext?.(handleBeforeNext);
	}, [onBeforeNext, handleBeforeNext]);

	const handleDismissWarning = useCallback(() => {
		setShowIncompleteWarning(false);
		resolveRef.current?.(false);
		resolveRef.current = null;
	}, []);

	const unsubmittedMembers = (members ?? [])
		.filter(isExpectedToScore)
		.filter((m) => m.status !== "declined" && m.status !== "revoked" && m.final_status !== "submitted");

	return (
		<div className="space-y-6" data-testid="final-evaluation-step">
			{state.isSubmitted && !allFinalScoresSubmitted && <InstructionsInfoBox title="Final Evaluation Scoring" instructions={FINAL_LEAD_INSTRUCTIONS} guideSteps={finalEvaluationLeadSteps} data-testid="final-instructions-info-box" />}
			<div className="flex border-b border-gray-200" data-testid="final-eval-tabs" role="tablist">
				{leadIsScoring && (
					<button
						type="button"
						role="tab"
						aria-selected={effectiveTab === "my-final-scores"}
						aria-controls="tab-panel-my-final-scores"
						onClick={() => setActiveTab("my-final-scores")}
						className={cn(
							"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
							effectiveTab === "my-final-scores"
								? "border-[#0031DD] text-[#0031DD]"
								: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
						)}
						data-testid="tab-my-final-scores"
					>
						My Final Scores
					</button>
				)}
				<button
					type="button"
					role="tab"
					aria-selected={effectiveTab === "panel-progress"}
					aria-controls="tab-panel-panel-progress"
					onClick={() => setActiveTab("panel-progress")}
					className={cn(
						"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
						effectiveTab === "panel-progress"
							? "border-[#0031DD] text-[#0031DD]"
							: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
					)}
					data-testid="tab-panel-progress"
				>
					Panel Members
				</button>
			</div>

			{leadIsScoring && <div
				id="tab-panel-my-final-scores"
				role="tabpanel"
				className={effectiveTab === "my-final-scores" ? "space-y-4" : "hidden"}
			>
				<FinalScoringContent rfxId={rfxId} evalId={evalId} />
			</div>}

			<div
				id="tab-panel-panel-progress"
				role="tabpanel"
				className={effectiveTab === "panel-progress" ? "space-y-6" : "hidden"}
			>
				<PanelEvaluationView
					rfxData={rfxData}
					evalId={evalId}
					isRfxLead
					mode="final"
					onCompletionChange={onCompletionChange}
					onRefreshUserContext={onRefreshUserContext}
				/>
			</div>

			{showIncompleteWarning && (
				<Modal
					isOpen={showIncompleteWarning}
					onClose={handleDismissWarning}
					title="Final Scores Not Complete"
					size="md"
					iconType="warning"
					modalType="standard"
					closeOnEsc
					closeOnOverlayClick={false}
					primaryButton={{
						text: "Go to Panel Members",
						onClick: () => {
							setShowIncompleteWarning(false);
							resolveRef.current?.(false);
							resolveRef.current = null;
							setActiveTab("panel-progress");
						},
						width: "full",
					}}
					secondaryButton={{
						text: "Close",
						onClick: handleDismissWarning,
						width: "full",
					}}
				>
					<div className="space-y-3">
						<p className="text-sm text-gray-600 text-center">
							All panel members must submit their final scores before you can proceed. Alternatively, you can revoke access for members who will not be submitting.
						</p>
						<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
							<div className="flex gap-2">
								<FiAlertTriangle size={16} className="shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
								<div className="text-sm text-amber-700 space-y-1">
									<p className="font-medium">The following members have not submitted:</p>
									<ul className="list-disc pl-4 space-y-0.5">
										{unsubmittedMembers.map((m) => (
											<li key={m.id}>{m.name || m.email}{m.is_current_user && " (you)"}</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};

interface FinalPanelMemberViewProps {
	evalId: number;
	rfxId: number;
}

const FinalPanelMemberView: React.FC<FinalPanelMemberViewProps> = ({
	rfxId,
	evalId,
}) => {
	return (
		<div data-testid="final-evaluation-step">
			<FinalScoringContent rfxId={rfxId} evalId={evalId} />
		</div>
	);
};
